import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./SearchParameters.module.scss";
import { Appliance } from "../../../model/appliance";
import { toast } from "react-toastify";
import FormWrapper from "../../../common/components/form-wrapper/FormWrapper";
import CommonFormFieldWrapper, { getFormInputErrors } from "../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import { useForm } from "react-hook-form";
import CommonFileUploadInput from "../../../common/components/file-upload-input/CommonFileUploadInput";
import { FileFromObjectUrl } from "../../../model/common/file-from-object-url";
import SearchResultItem from "../../customer/solar-recommendation/components/add-appliances/components/SearchResultItem";
import CommonSelect, { CommonSelectItem } from "../../../common/components/select/CommonSelect";
import { useLazyGetApplianceCategoriesQuery } from "../../../service/appliance-category";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../stores/slices/loader-slice";
import { 
    useLazyGetAppliancesQuery,
    useAddApplianceMutation,
    useEditApplianceMutation,
    useDeleteApplianceMutation,
    prepareApplianceFormData,
    prepareEditApplianceFormData
} from "../../../service/appliance-service";
import { getErrorMessage } from "../../../utils/error-handler";
import ConfirmationDialog from "../../../common/components/dialog/ConfirmationDialog";

type Props = {};

type newApplianceForm = {
	id: number;
	name: string;
	power: number;
	iconEdited: boolean;
	iconUrl: FileFromObjectUrl;
	icon: File;
    applianceCategoryId: string;
};

const SearchParameters: React.FC<Props> = (props: Props) => {
	const [search, setSearch] = useState<string>("");
	const [debouncedSearch, setDebouncedSearch] = useState<string>("");
	const [isFormVisible, setIsFormVisible] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [selectedAppliance, setSelectedAppliance] = useState<Appliance | null>(null);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	
    const dispatch = useDispatch ();

	const [getAppliances, { data: appliances = [], isLoading: isLoadingAppliances }] = useLazyGetAppliancesQuery();
    const [addAppliance] = useAddApplianceMutation();
    const [editAppliance] = useEditApplianceMutation();
    const [deleteApplianceMutation, { isLoading: isDeleting }] = useDeleteApplianceMutation();

    const [getCategories, { data, isLoading }] = useLazyGetApplianceCategoriesQuery()
    const Categories = useMemo(() => {
		return  data?.map(item => new CommonSelectItem(item.id, item.name)) || [];
	}, [data]);

	useEffect(() => {
		getCategories();
	 }, [getCategories]);


	 const fetchAppliances = useCallback(async () => {
		try {
			await getAppliances(debouncedSearch);
		} catch (error) {
			toast.error("There was an error fetching the appliances");
		}
	}, [debouncedSearch, getAppliances]);

	function containsSearch() {
		if (debouncedSearch) {
			return styles.searchContainesText;
		}
	}

	const addNewAppliance = async () => {
		try {
			const obj = getValues();
            dispatch(showLoader());
            
            const formData =  prepareApplianceFormData({
                name: obj.name.trim(),
                power: obj.power,
                icon: obj.icon,
                applianceCategoryId: obj.applianceCategoryId
            });
            
			await addAppliance(formData).unwrap();
			await getAppliances(debouncedSearch);
            
            toast.success('Appliance added successfully');
            dispatch(hideLoader());
			setIsFormVisible(false);
		} catch (error: any) {
			toast.error(getErrorMessage(error.data));
            dispatch(hideLoader());
		}
	};

	const submitEditAppliance = async () => {
		try {
			const obj = getValues();
            dispatch(showLoader());
            
            const formData = await prepareEditApplianceFormData({
                id: String(obj.id),
                name: obj.name.trim(),
                power: obj.power,
                applianceCategoryId: obj.applianceCategoryId,
                iconEdited: obj.iconEdited,
                iconUrl: obj.iconUrl
            });
            
			await editAppliance(formData).unwrap();
			await getAppliances(debouncedSearch);
            
            dispatch(hideLoader());
            toast.success('Appliance edited successfully');
			setIsFormVisible(false);
		} catch (error) {
            dispatch(hideLoader());
			toast.error("There was an error editing the appliance");
		}
	};

	const editApplianceHandler = async (appliance: Appliance) => {
		setIsEdit(true);
		setIsFormVisible(true);

        const selectedCategory = Categories.find(
            category => category.key === appliance?.applianceCategoryId
        );

		reset({
			id: appliance.id,
			name: appliance.name,
			power: appliance.power,
			iconEdited: false,
			iconUrl: {
				url: appliance.iconUrl,
				name: appliance.iconUrl.split("/").pop()!,
			},
            applianceCategoryId: appliance.applianceCategoryId,
		});

        if (selectedCategory) {
            OnCategorySelected(selectedCategory);
        }
	};

	const handleDeleteAppliance = (appliance: Appliance) => {
		setSelectedAppliance(appliance);
		setShowDeleteConfirmation(true);
	};

	const confirmDeleteAppliance = async () => {
		try {
			if (!selectedAppliance) return;
			
			dispatch(showLoader());
			await deleteApplianceMutation(String(selectedAppliance.id)).unwrap();
			await getAppliances(debouncedSearch);
			
			toast.success('Appliance deleted successfully');
			setShowDeleteConfirmation(false);
			setSelectedAppliance(null);
			dispatch(hideLoader());
		} catch (error: any) {
			toast.error(getErrorMessage(error.data) ?? "There was an error deleting the appliance");
			dispatch(hideLoader());
		}
	};

	function changeSearch(e: React.ChangeEvent<HTMLInputElement>) {
		setSearch(e.target.value.trim());
	}

	const onFilesSelection = (files: File[]) => {
		if (files.length === 0) {
			return;
		}

		const fileToSend: FileFromObjectUrl = {
			name: files[0].name,
			url: URL.createObjectURL(files[0]),
		};
		setValue("icon", files[0]);
		setValue("iconUrl", fileToSend, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
		setValue("iconEdited", true);
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 500);

		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		fetchAppliances();
	}, [fetchAppliances]);

	const {
		register,
		watch,
		formState: { errors, isValid },
		setValue,
		getValues,
		reset,
	} = useForm<newApplianceForm>({
		defaultValues: {
			name: undefined,
			power: undefined,
			iconUrl: {
				name: "",
				url: "",
			},
            applianceCategoryId: undefined,
		},
		reValidateMode: "onSubmit",
		mode: "onTouched",
	});

	const cancelForm = () => {
		setIsFormVisible(false);
		reset();
	};

    const OnCategorySelected = (item: CommonSelectItem | undefined) => {
        setValue("applianceCategoryId", item?.key, { 
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true 
        });
    };

	const openNewForm = () => {
		setIsEdit(false);
		reset({
			name: "",
			power: 0,
			iconEdited: false,
			iconUrl: undefined,
		});
		setIsFormVisible(true);
	};

	return (
		<>
		
		{showDeleteConfirmation && (
        <ConfirmationDialog
            actionLoading={isDeleting}
            btnText="Delete"
            onCloseDialog={() => {
                setShowDeleteConfirmation(false);
                setSelectedAppliance(null);
            }}
            onConfirm={confirmDeleteAppliance}
            title="Delete Appliance"
            description={`Are you sure you want to delete ${selectedAppliance?.name}?`}
        />
    )}
		<div className={styles.container}>
			<div className={styles.leftSide}>
				<FormWrapper
					titleSection={{ title: "All Appliances", subtitle: "Search and select an appliance to continue.", buttonTitle: "Add" }}
					onSaveAndContinue={openNewForm}
				>
					<div className={styles.findApplianceContainer}>
						<div className={`${styles.searchContainer} ${containsSearch()}`}>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M15.2825 14.7176L12.5001 11.9576C13.5801 10.6109 14.1032 8.90152 13.9617 7.18103C13.8201 5.46053 13.0248 3.85964 11.7392 2.70753C10.4536 1.55543 8.77541 0.939683 7.04974 0.986903C5.32408 1.03412 3.68209 1.74072 2.46141 2.96141C1.24072 4.18209 0.534124 5.82408 0.486903 7.54974C0.439683 9.27541 1.05543 10.9536 2.20753 12.2392C3.35964 13.5248 4.96053 14.3201 6.68103 14.4617C8.40152 14.6032 10.1109 14.0801 11.4576 13.0001L14.2176 15.7601C14.2873 15.8303 14.3702 15.8861 14.4616 15.9242C14.553 15.9623 14.651 15.9819 14.7501 15.9819C14.8491 15.9819 14.9471 15.9623 15.0385 15.9242C15.1299 15.8861 15.2128 15.8303 15.2825 15.7601C15.4177 15.6202 15.4933 15.4333 15.4933 15.2388C15.4933 15.0443 15.4177 14.8574 15.2825 14.7176ZM7.25005 13.0001C6.2117 13.0001 5.19666 12.6921 4.33331 12.1153C3.46995 11.5384 2.79704 10.7185 2.39968 9.75914C2.00232 8.79983 1.89836 7.74423 2.10093 6.72583C2.3035 5.70743 2.80351 4.77197 3.53774 4.03774C4.27197 3.30351 5.20743 2.8035 6.22583 2.60093C7.24423 2.39836 8.29983 2.50232 9.25914 2.89968C10.2185 3.29704 11.0384 3.96995 11.6153 4.83331C12.1921 5.69666 12.5001 6.7117 12.5001 7.75005C12.5001 9.14244 11.9469 10.4778 10.9624 11.4624C9.97779 12.4469 8.64244 13.0001 7.25005 13.0001Z"
									fill="#2E3336"
								/>
							</svg>
							<input placeholder="Search amenities" onChange={changeSearch} />
						</div>
						<div className={styles.searchResultContainer}>
							{appliances.map((appliance, index) => (
								<SearchResultItem
									key={appliance.id}
									appliance={appliance}
									isAdded
									onEditApplianceHandler={() => editApplianceHandler(appliance)}
									onDeleteApplianceHandler={() => handleDeleteAppliance(appliance)}
								/>
							))}
						</div>
					</div>
				</FormWrapper>
			</div>
			{isFormVisible && (
				<div className={styles.rightSide}>
					<FormWrapper
						titleSection={{ title: isEdit ? "Edit appliance" : "Create new appliance", subtitle: "", buttonTitle: "Save Changes" }}
						onBack={cancelForm}
						isNotValid={!isValid}
						onSaveAndContinue={() => (!isEdit ? addNewAppliance() : submitEditAppliance())}
					>
						<div className={styles.formSection}>
							<h2>Select Appliance Category</h2>
							<CommonFormFieldWrapper title="Appliance Category" required errors={getFormInputErrors(errors.applianceCategoryId?.message)}>
								<CommonSelect
									{...register("applianceCategoryId", {
										required: "Product category is required",
									})}
									onChange={OnCategorySelected}
									items={Categories}
									placeholder="Select category"
									searchable
                                    selected={Categories.find(
                                        category => category.key === watch("applianceCategoryId")
                                    )}
								/>
							</CommonFormFieldWrapper>
							<CommonFormFieldWrapper title="Appliance Name" required errors={getFormInputErrors(errors.name?.message)}>
								<input {...register("name", { required: "Appliance Name is required" })} placeholder="Enter Appliance Name" type="text" />
							</CommonFormFieldWrapper>
							<CommonFormFieldWrapper title="Appliance Wattage" required errors={getFormInputErrors(errors.power?.message)}>
								<input
									{...register("power", {
										required: "Appliance Wattage is required",
										validate: value => value > 0 || "Appliance Wattage must be greater than 0",
									})}
									placeholder="Enter Appliance Wattage"
									type="number"
									min={0}
								/>
							</CommonFormFieldWrapper>
							<CommonFormFieldWrapper title="Appliance Image" required errors={getFormInputErrors(errors.iconUrl?.message)}>
								<div className={styles.fileInputContainer}>
									<CommonFileUploadInput
										maxFileSize={24}
										initialFiles={[watch("iconUrl.name")]}
										accept="image/*"
										registerInputProps={register("iconUrl", { required: "Appliance Image is required" })}
										onFilesSelect={onFilesSelection}
									/>
								</div>
							</CommonFormFieldWrapper>
						</div>
					</FormWrapper>
				</div>
			)}
		</div>
		</>
	);
};

export default SearchParameters;
